<script setup lang="ts">
import { useAuthStore } from '~/stores/auth.store';
import Button from '~/components/design-system/button.vue';
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/solid';
import { BanknotesIcon } from '@heroicons/vue/24/outline';
import { useSiteStore } from '~/stores/site.store.js';
import RestrictedAccessTag from '~/components/design-system/restricted-access-tag.vue';
import type { IHamburgerMenuState } from '~/interfaces/components/layout';

interface IUserBalances {
  displayBalanceToggle?: boolean;
  displayRefreshBalance?: boolean;
  displayAccountBalanceToggle?: boolean;
  displayCashBalance?: boolean;
  displayBonusBalance?: boolean;
  // displayTotalBalance: boolean;  TODO: Remove when confirmed Total Balance is no longer required
  displayDepositButton?: boolean;
  displayRestrictedTag?: boolean;
  hideCompleteAccount?: boolean;
  displayRewardsBalance?: boolean;
  depositButtonStyle?: 'full' | 'icon' | 'modal';
  depositButtonFallback?: 'icon' | 'text';
  stretch?: boolean;
  accountOptionsModalStyle?: boolean;
  backgroundColor?: 'bg-base-secondary' | 'bg-base';
  singleLine?: boolean;
  displayTruncatedBalance?: boolean;
  rounding?: 'md' | 'lg' | 'xl';
  forceHideCompleteAccount?: boolean;
}

const { $walletService, $authService } = useServices();
const auth = useAuthStore();
const site = useSiteStore();
const rg = useRgStore();

const { $enabled } = useNuxtApp();

const props = withDefaults(defineProps<IUserBalances>(), {
  displayBalanceToggle: false,
  displayRefreshBalance: false,
  displayAccountBalanceToggle: false,
  displayCashBalance: false,
  displayBonusBalance: false,
  displayRewardsBalance: false,
  // displayTotalBalance: false,  TODO: Remove when confirmed Total Balance is no longer required
  displayDepositButton: false,
  displayRestrictedTag: false,
  hideCompleteAccount: false,
  depositButtonStyle: 'icon',
  depositButtonFallback: 'icon',
  stretch: false,
  accountOptionsModalStyle: false,
  backgroundColor: 'bg-base-secondary',
  singleLine: false,
  displayTruncatedBalance: false,
  rounding: 'md',
  forceHideCompleteAccount: false,
});

const emits = defineEmits<{
  (e: 'openDeposit'): void;
}>();

const hamburgerMenuState = inject<IHamburgerMenuState>('hamburgerMenuState');

const loading = computed(
  () => !auth.userBalances?.cashBalance && auth.userBalances?.cashBalance !== 0,
);

const balancesEnabledCount = computed(() => {
  const count = ref(0);
  if (showCashBalance.value) count.value++;
  if (showBonusBalance.value) count.value++;
  if (showRewardsBalance.value) count.value++;
  return count.value;
});

const breakpoint = useBreakpoint();
const state = reactive({
  renderSelector: breakpoint.sm,
});

const showCompleteAccount = computed(
  () =>
    !!auth.currentUser &&
    site.getFeatureFlags.get('myaccount.enablecompliance') &&
    !props.hideCompleteAccount &&
    (!auth.getIsUserCompliant ||
      (rg.hasPartialRegLock && $enabled('myaccount.enablepartialreg'))) &&
    !(
      auth.getAccountModalPage === 'withdrawal' ||
      auth.getAccountModalPage === 'deposit'
    ),
);

const displayRestricted = computed(() => {
  return (
    rg.accountLocked && props.displayRestrictedTag && !showCompleteAccount.value
  );
});

const showCashBalance = computed(
  () => props.displayCashBalance && $enabled('myaccount.cashbalance'),
);
const showBonusBalance = computed(
  () => props.displayBonusBalance && $enabled('myaccount.bonusbalance'),
);
const showRewardsBalance = computed(
  () => props.displayRewardsBalance && $enabled('myaccount.enablerewards'),
);

const isAllBalancesShowing = computed(
  () =>
    showCashBalance.value && showBonusBalance.value && showRewardsBalance.value,
);

const refreshBalances = async () => {
  await $walletService.fetchBalances();
  if ($enabled('myaccount.enablerewards')) {
    await $authService.getRewardsSummary();
  }
};

const handleCompleteAccount = () => {
  if (!auth.getIsRegistrationComplete && $enabled('myaccount.enablepartialreg'))
    site.activateModal('complete-registration');
  else {
    site.deactivateModal();
    auth.setAccountModalPage('document-verification');
  }
  hamburgerMenuState.active = false;
};

const showUserbalances = computed(
  () =>
    auth.getIsUserCompliant ||
    !site.getFeatureFlags.get('myaccount.enablecompliance') ||
    $enabled('myaccount.enablepartialreg'),
);
</script>

<template>
  <div
    v-if="showUserbalances"
    :class="[
      { 'mt-2': accountOptionsModalStyle },
      'flex justify-center items-center gap-1',
      $attrs?.class,
    ]"
  >
    <Button
      v-if="displayBalanceToggle && !isAllBalancesShowing"
      type="transparent"
      padding="none"
      class="mr-2 hover:shadow-none"
      @click="auth.toggleBalances(!auth.displayAccountBalances)"
    >
      <EyeSlashIcon v-if="auth.displayAccountBalances" class="w-4" />
      <EyeIcon v-else class="w-4" />
    </Button>
    <div
      v-if="balancesEnabledCount > 0"
      :class="[balancesEnabledCount > 2 ? 'flex-col' : '', 'flex gap-2 w-full']"
    >
      <div :class="[{ 'flex flex-row': balancesEnabledCount === 2 }, 'w-full']">
        <UserBalance
          v-if="showCashBalance"
          :loading="loading"
          :label="
            $t(
              accountOptionsModalStyle && state.renderSelector
                ? 'cash.balance'
                : 'cash-balance',
            )
          "
          :hide-balance="!auth.displayAccountBalances"
          :amount="auth?.accountBalances?.cashBalance"
          :error="auth.accountBalances?.cashBalance < 0"
          :toggle-refresh-balances="displayRefreshBalance"
          :toggle-balance-modal="displayAccountBalanceToggle"
          :is-account-modal="accountOptionsModalStyle"
          :background-color="backgroundColor"
          :single-line="singleLine"
          :truncate-balance="displayTruncatedBalance"
          :class="`rounded-${props.rounding}`"
          @refresh-balances="refreshBalances"
        />
      </div>
      <div
        v-if="showBonusBalance || showRewardsBalance"
        :class="[
          { 'flex flex-row gap-2': balancesEnabledCount === 3 },
          { 'w-full': balancesEnabledCount > 1 },
        ]"
      >
        <UserBalance
          v-if="showBonusBalance"
          :amount="
            auth.displayBonusBalance
              ? auth?.accountBalances?.playThroughBalance
              : 0
          "
          :background-color="backgroundColor"
          :error="auth.activeBonus?.totalAwardedBonusAmount < 0"
          :hide-balance="!auth.displayAccountBalances"
          :is-account-modal="accountOptionsModalStyle"
          :label="$t('bonus-balance')"
          :loading="loading"
          :single-line="singleLine"
          :toggle-balance-modal="displayAccountBalanceToggle"
          :toggle-refresh-balances="displayRefreshBalance"
          :truncate-balance="displayTruncatedBalance"
          :class="`rounded-${props.rounding} w-full`"
          @refresh-balances="refreshBalances"
        />

        <UserBalance
          v-if="showRewardsBalance"
          :loading="loading"
          :label="$t('jpc-rewards')"
          :hide-balance="!auth.displayAccountBalances"
          :amount="auth.getRewardsBalance.amount"
          :error="auth.getRewardsBalance.amount < 0"
          :toggle-refresh-balances="displayRefreshBalance"
          :toggle-balance-modal="displayAccountBalanceToggle"
          :is-account-modal="accountOptionsModalStyle"
          :background-color="backgroundColor"
          :single-line="singleLine"
          :truncate-balance="displayTruncatedBalance"
          is-rewards-balance
          :class="`rounded-${props.rounding} w-full`"
          @refresh-balances="refreshBalances"
        />
      </div>
    </div>
    <Button
      v-if="
        displayDepositButton &&
        !isAllBalancesShowing &&
        !displayRestricted &&
        depositButtonStyle !== 'modal'
      "
      type="other"
      class="flex-center"
      :class="{
        'w-full max-w-max h-10': depositButtonStyle === 'full',
        'w-full max-w-10 max-h-10 aspect-square': depositButtonStyle !== 'full',
        'ml-1': displayBalanceToggle,
      }"
      :rounding="props.rounding"
      @click="[
        site.deactivateModal(),
        auth.setAccountModalPage('deposit'),
        emits('openDeposit'),
      ]"
    >
      <div
        v-if="depositButtonStyle === 'full'"
        :class="[
          { 'hidden md:block': depositButtonFallback === 'icon' },
          'text-xxs',
        ]"
      >
        {{ $t('deposit') }}
      </div>
      <BanknotesIcon
        :class="[
          {
            'hidden md:block ml-1':
              depositButtonFallback === 'text' && depositButtonStyle === 'full',
          },
        ]"
        class="w-5"
      />
    </Button>
    <div
      v-if="isAllBalancesShowing"
      :class="[
        { 'self-start': depositButtonStyle === 'modal' },
        'flex flex-col items-start justify-between gap-2 ml-1',
      ]"
    >
      <Button
        v-if="
          displayDepositButton &&
          !displayRestricted &&
          depositButtonStyle !== 'modal'
        "
        type="other"
        class="flex-center leading-5"
        :class="{
          'w-full max-w-[110px]': depositButtonStyle === 'full',
          'w-full max-w-10 max-h-10': depositButtonStyle !== 'full',
        }"
        :rounding="props.rounding"
        padding="wide"
        @click="[
          site.deactivateModal(),
          auth.setAccountModalPage('deposit'),
          emits('openDeposit'),
        ]"
      >
        <div
          v-if="depositButtonStyle === 'full'"
          :class="[
            { 'hidden md:block': depositButtonFallback === 'icon' },
            'ml-1 mr-1 text-xxs',
          ]"
        >
          {{ $t('deposit') }}
        </div>
        <BanknotesIcon
          :class="[
            {
              'hidden md:block':
                depositButtonFallback === 'text' &&
                depositButtonStyle === 'full',
            },
          ]"
          class="w-5"
        />
      </Button>
      <Button
        v-if="displayBalanceToggle"
        type="transparent"
        class="pl-0 hover:shadow-none"
        @click="auth.toggleBalances(!auth.displayAccountBalances)"
      >
        <EyeSlashIcon v-if="auth.displayAccountBalances" class="w-4" />
        <EyeIcon v-else class="w-4" />
      </Button>
    </div>
  </div>
  <div
    v-if="
      showUserbalances &&
      site.modalActive &&
      displayDepositButton &&
      depositButtonStyle === 'modal'
    "
    class="bg-layer p-2 flex-center"
  >
    <Button
      type="other"
      class="flex-center w-full"
      :rounding="props.rounding"
      @click="[
        site.deactivateModal(),
        auth.setAccountModalPage('deposit'),
        emits('openDeposit'),
      ]"
    >
      {{ $t('deposit') }}
    </Button>
  </div>
  <div
    v-if="showCompleteAccount || displayRestricted"
    class="flex gap-2 items-center"
    :class="{
      'my-1':
        $enabled('myaccount.enablepartialreg') && hamburgerMenuState.active,
      'mt-3': showCompleteAccount && hamburgerMenuState.active,
    }"
  >
    <Button
      v-if="showCompleteAccount"
      :class="[
        'min-h-10',
        {
          'w-full': stretch,
          'w-max': !stretch,
          'hidden xsm:block':
            $enabled('myaccount.enablepartialreg') && forceHideCompleteAccount,
        },
      ]"
      :rounding="props.rounding"
      padding="wide-short"
      @click="handleCompleteAccount"
    >
      <div class="text-wrap text-center w-full text-xs">
        {{ $t('complete-your-account') }}
      </div>
    </Button>
    <RestrictedAccessTag v-else-if="displayRestricted" />
  </div>
</template>
